import React from 'react';
import { useDispatch } from 'react-redux';
import { strings } from '../../../resources/strings';
import { MainActions } from '../../../actions/MainActions';
import ServicesContext from '../../../contexts/ServicesContext';
import { federativeUnits } from '../../../services/FederativeUnits/FederativeUnitsService';
import { Field, FieldType } from '../../../common-components/Field/Field';
import { WaitingIcon } from '../../../common-components/WaitingIcon/WaitingIcon';
import { Row, NoBreakRow, RightColumn } from './styles';
import { Button } from '../../../common-components/Button/Button';

export type CustomerAddressType = {
    zip?: string;
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    additionalAddress?: string;
    city?: string;
    provinceCode?: string;
}

interface ITabAddressProps {
    address?: CustomerAddressType|null;
    setAddress: (address: CustomerAddressType) => void;
}

const TabAddress: React.FC<ITabAddressProps> = ({
    address,
    setAddress
}) => {

    const dispatch = useDispatch();
    const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

    const {
        getAddressesByZipService,
        toastService
    } = React.useContext(ServicesContext);

    const [ searchingZip, setSearchingZip ] = React.useState<boolean>(false);

    const federativeUnitOptions = [ {abbreviation: ' ', name: ''}, ...federativeUnits ];

    const body = React.useMemo(() => {
        if (searchingZip) {
            return (
                <WaitingIcon />
            );
        }

        return (
            <>
                <NoBreakRow justifyContent='start'>
                    <Field
                        type={FieldType.Mask}
                        label={`${strings.Zip}`}
                        value={address?.zip}
                        change={(value) => setAddress( address ? { ...address, zip: value } : { zip: value })}
                        marginTop={'0px'}
                        width='calc(50% - 5px)'
                        widthMobile='100%'
                        mask='00000-000'
                        autoFocus={true}
                    />
                    <RightColumn>
                        <Button
                            text={strings.Consult}
                            borderRadius='20px'
                            marginTop='22px'
                            minWidth='100px'
                            minHeight='38px'
                            click={() => getAddressByZip(true)}
                        />
                    </RightColumn>
                </NoBreakRow>
                <Row>
                    <Field
                        type={FieldType.Text}
                        label={strings.PublicPlace}
                        value={address?.address}
                        change={(value) => setAddress( address ? { ...address, address: value } : { address: value })}
                        marginTop={'0px'}
                        marginRight='5px'
                        marginRightMobile='0px'
                    />
                    <Field
                        type={FieldType.Text}
                        label={strings.Number}
                        value={address?.addressNumber}
                        change={(value) => setAddress( address ? { ...address, addressNumber: value } : { addressNumber: value })}
                        marginLeft='5px'
                        marginLeftMobile='0px'
                        marginTop={'0px'}
                    />
                </Row>
                <Row>
                    <Field
                        type={FieldType.Text}
                        label={strings.Neighborhood}
                        value={address?.neighborhood}
                        change={(value) => setAddress( address ? { ...address, neighborhood: value } : { neighborhood: value })}
                        marginLeftMobile='0px'
                        marginTop={'0px'}
                    />
                </Row>
                <Row>
                    <Field
                        type={FieldType.Text}
                        label={strings.AdditionalAddress}
                        value={address?.additionalAddress}
                        change={(value) => setAddress( address ? { ...address, additionalAddress: value } : { additionalAddress: value })}
                        marginLeftMobile='0px'
                        marginTop={'0px'}
                    />
                </Row>
                <NoBreakRow>
                    <Field
                        type={FieldType.Text}
                        label={strings.City}
                        value={address?.city}
                        change={(value) => setAddress( address ? { ...address, city: value } : { city: value })}
                        marginTop={'0px'}
                        marginRight='5px'
                    />
                    <Field
                        type={FieldType.Select}
                        label={strings.State}
                        selectProps={{
                            options: federativeUnitOptions,
                            displayField: 'abbreviation',
                            keyField: 'abbreviation'
                        }}
                        value={address?.provinceCode || ' '}
                        change={(value) => setAddress( address ? { ...address, provinceCode: value } : { provinceCode: value })}
                        marginLeft='5px'
                        marginTop={'0px'}
                        width='30%'
                    />
                </NoBreakRow>
            </>
        );
    }, [searchingZip, address, setAddress]);

    return (
        <>
            {body}
        </>
    );

    function getAddressByZip(enforce?: boolean) {
        if (!address?.zip || address.zip.length < 8) {
            toastService.warning(strings.InformeTheZipCode);
            return;
        }

        if (
            !enforce && 
            (address.address || address.neighborhood || 
             address.addressNumber || address.city || 
             address.provinceCode)
        ) return;

        mainActions.setNotWait(true);
        setSearchingZip(true);

        getAddressesByZipService.handle({ zip: address.zip.toString() })
            .then(result => {
                const firstAddress = result?.value?.addressList && result?.value?.addressList.length > 0 ? result?.value?.addressList[0] : null;
                
                if (!firstAddress) return;

                setAddress({
                    zip: address.zip,
                    address: firstAddress.logradouro,
                    additionalAddress: firstAddress.complemento,
                    neighborhood: firstAddress.bairro,
                    city: firstAddress.localidade,
                    provinceCode: firstAddress.uf || ' '
                });
            })
            .finally(() => {
                setSearchingZip(false);
                setTimeout(() => mainActions.setNotWait(false), 1000);
            });
    }
}

export {TabAddress};